import { useEffect, useState, type ReactNode } from 'react';
import type { CrudFilter } from '@refinedev/core';
import { Select, type SelectProps } from 'antd';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { FilterPanel } from './FilterPanel';

export type FilterValue = string | number | boolean;

/** antd narrows option values to string|number, so booleans need our own type. */
export type FilterOption<T extends FilterValue> = {
  label: ReactNode;
  value: T;
};

export type SelectFilterProps<T extends FilterValue> = {
  field: string;
  options: FilterOption<T>[];
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
  minWidth?: number;
  /**
   * Extra antd `Select` props, e.g. `useSelect`'s `showSearch` + `onSearch` for
   * server-side lookup. `value`/`onChange`/`options` stay owned by this component.
   */
  selectProps?: Omit<
    SelectProps<T, FilterOption<T>>,
    'value' | 'onChange' | 'options'
  >;
};

/**
 * Single-choice column filter, applied as `field[eq]`.
 *
 * Writes through `setFilters` rather than antd's own filter state — see
 * `useAdminTable` for why the two must not be mixed.
 */
export const SelectFilter = <T extends FilterValue>({
  field,
  options,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
  minWidth = 160,
  selectProps,
}: SelectFilterProps<T>) => {
  const current = getFilterValue<T>(filters, field, 'eq');
  const [pending, setPending] = useState<T | undefined>(current);

  useEffect(() => setPending(current), [current]);

  const handleFilter = () => {
    setFilters([{ field, operator: 'eq', value: pending }], 'merge');
    confirm();
  };

  const handleReset = () => {
    setPending(undefined);
    setFilters([{ field, operator: 'eq', value: undefined }], 'merge');
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <Select<T, FilterOption<T>>
        allowClear
        placeholder={placeholder}
        {...selectProps}
        style={{ minWidth, ...selectProps?.style }}
        value={pending}
        onChange={(value?: T) => setPending(value)}
        options={options}
      />
    </FilterPanel>
  );
};
